import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, Save, Loader2 } from 'lucide-react';
import { Button, ButtonVariant } from '@meiuca/ui-components';
import { UserLocationAutocomplete } from '../../components/UserLocationAutocomplete';
import { useUserLocation, UserLocation } from '../../hooks/useUserLocation';
import { toastManager } from '../../lib/toastManager';
import { ServiceFactory } from '../../services/ServiceFactory';

export default function LocationSettingsPage() {
  const navigate = useNavigate();
  const { userLocation, updateUserLocation, isLoading, getFullLocationName } = useUserLocation();
  const [selectedLocation, setSelectedLocation] = useState<UserLocation | null>(userLocation);
  const [isSaving, setIsSaving] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const addressService = ServiceFactory.createAddressService();

  const getCurrentLocation = async () => {
    console.log('🚀 Iniciando getCurrentLocation...');
    setIsGettingLocation(true);
    
    try {
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        throw new Error('Geolocalização não é suportada neste navegador');
      }
      
      console.log('📍 Geolocalização suportada, chamando addressService...');
      const address = await addressService.getCurrentLocation();
      console.log('✅ Endereço recebido:', address);
      
      // Convert Address to UserLocation
      const userLocationData: UserLocation = {
        placeId: address.id,
        description: address.formattedAddress,
        mainText: address.neighborhood || address.city || address.street || 'Local',
        secondaryText: `${address.city}, ${address.state}`,
        coordinate: {
          latitude: address.coordinates?.lat || 0,
          longitude: address.coordinates?.lng || 0
        }
      };
      
      console.log('🔄 UserLocation convertida:', userLocationData);
      setSelectedLocation(userLocationData);
      toastManager.success('📍 Localização atual obtida com sucesso!');
      
    } catch (error: any) {
      console.error('❌ Erro completo ao obter localização:', error);
      
      // Mensagens mais específicas baseadas no tipo de erro
      let errorMessage = 'Erro ao obter sua localização atual';
      
      if (error.message?.includes('User denied')) {
        errorMessage = 'Permissão de localização negada. Por favor, permita o acesso à localização nas configurações do navegador.';
      } else if (error.message?.includes('Timeout')) {
        errorMessage = 'Tempo limite para obter localização. Tente novamente.';
      } else if (error.message?.includes('suportada')) {
        errorMessage = 'Seu navegador não suporta geolocalização.';
      } else if (error.message?.includes('HTTPS')) {
        errorMessage = 'Geolocalização requer uma conexão segura (HTTPS).';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toastManager.error(errorMessage);
      
    } finally {
      console.log('🏁 Finalizando getCurrentLocation');
      setIsGettingLocation(false);
    }
  };

  const handleSave = async () => {
    if (!selectedLocation) {
      toastManager.error('Selecione uma localização');
      return;
    }

    try {
      setIsSaving(true);
      updateUserLocation(selectedLocation);
      toastManager.success('📍 Localização selecionada com sucesso!');
      navigate(-1);
    } catch (error) {
      toastManager.error('Erro ao selecionar localização');
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = selectedLocation?.placeId !== userLocation?.placeId;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center space-x-4 mb-8"
      >
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg bg-background-white border border-border-primary text-text-secondary hover:bg-background-secondary transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        
        <div>
          <h1 className="text-2xl font-bold text-text-primary">
            Configurar Localização
          </h1>
          <p className="text-text-secondary">
            Esta será sua localização principal para buscar jogos próximos
          </p>
        </div>
      </motion.div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="space-y-6"
      >
        {/* Current Location */}
        {userLocation && (
          <div className="p-4 bg-background-white rounded-xl border border-border-primary">
            <div className="flex items-center space-x-3 mb-2">
              <MapPin className="w-5 h-5 text-brand-primary" />
              <span className="font-medium text-text-primary">Localização Atual</span>
            </div>
            <div className="text-sm text-text-secondary ml-8">
              {getFullLocationName(userLocation)}
            </div>
          </div>
        )}

        {/* Location Search */}
        <div className="p-6 bg-background-white rounded-xl border border-border-primary">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Buscar nova localização
              </label>
              <div className="space-y-3">
                <UserLocationAutocomplete
                  value={selectedLocation}
                  onChange={setSelectedLocation}
                  placeholder="Digite o nome do bairro, cidade ou local..."
                />
                
                {/* Current Location Button */}
                <Button
                  variant={ButtonVariant.PRIMARY}
                  buttonStyle="clear"
                  onClick={getCurrentLocation}
                  disabled={isGettingLocation || isSaving}
                  className="w-full flex items-center justify-center gap-2 border border-brand-primary text-brand-primary hover:bg-brand-primary/5"
                >
                  {isGettingLocation ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <MapPin className="w-4 h-4" />
                  )}
                  {isGettingLocation ? 'Obtendo localização...' : 'Usar minha localização atual'}
                </Button>
              </div>
            </div>
            
            {selectedLocation && (
              <div className="p-3 bg-brand-primary/5 rounded-lg border border-brand-primary/20">
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-brand-primary mt-0.5" />
                  <div>
                    <div className="font-medium text-brand-primary">
                      {selectedLocation.mainText}
                    </div>
                    <div className="text-sm text-brand-primary/70">
                      {selectedLocation.secondaryText}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          <Button
            variant={ButtonVariant.PRIMARY}
            buttonStyle="clear"
            onClick={() => navigate(-1)}
            className="flex-1"
          >
            Cancelar
          </Button>
          
          <Button
            variant={ButtonVariant.PRIMARY}
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className="flex-1"
          >
            {isSaving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isSaving ? 'Selecionando...' : 'Selecionar Localização'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}